import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Plus, Pencil, Trash2 } from "lucide-react";
import { api, idr, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import InputRupiah from "@/components/InputRupiah";
import { DatePicker } from "@/components/ui/date-picker";
import { formatTanggal, hariIniISO } from "@/lib/tanggal";
import {
  PageHeader, TidakPunyaAkses, ADS_PLATFORMS, PLATFORM_LABEL, SortTh, useSortState,
  sortRows, usePaginasi, Paginasi, unduhCsv, TombolEksporCsv, Modal, Field, inputCls,
  selectCls, textareaCls, useCampaignMap, namaCampaign, usePinHapus, PinHapusDialog,
  SearchInput, FilterSelect, FilterRentangTanggal, TombolResetFilter, useOpsiFilter,
  adaFilterAktif, paramFilter,
} from "./_shared";

const emptyForm = () => ({
  campaign_id: "", platform: ADS_PLATFORMS[0], tanggal: hariIniISO(),
  spend: 0, impressions: 0, clicks: 0, leads: 0, conversions: 0, video_views: 0, engagements: 0, catatan: "",
});

export default function Ads() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_ads", "view");
  const bolehTambah = can("marketing_ads", "create");
  const bolehEdit = can("marketing_ads", "update");
  const bolehHapus = can("marketing_ads", "delete");
  const bolehCampaign = can("marketing_campaign", "view");
  const { campaignMap, campaignList } = useCampaignMap(bolehCampaign);
  const [searchParams] = useSearchParams();
  const campaignPreset = searchParams.get("campaign_id") || "";

  const [campaignFilter, setCampaignFilter] = useState(campaignPreset);
  const [platformFilter, setPlatformFilter] = useState("");
  const [pembuatFilter, setPembuatFilter] = useState("");
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("tanggal", "desc");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();
  const opsi = useOpsiFilter("/marketing/ads-logs/opsi-filter", bolehLihat);

  // Semua filter dikerjakan di server (daftar dipotong 5000 baris di backend).
  // Tanggal berformat salah dijawab 400 oleh backend, bukan diam-diam jadi
  // daftar kosong yang terlihat seperti data hilang.
  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "ads-logs", campaignFilter, platformFilter, pembuatFilter, qDebounced, dateFrom, dateTo],
    () => api.get("/marketing/ads-logs", {
      params: paramFilter({
        campaign_id: campaignFilter, platform: platformFilter,
        dibuat_oleh: pembuatFilter, q: qDebounced.trim(),
        date_from: dateFrom, date_to: dateTo,
      }),
    }).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const { data: summary } = useDataQuery(
    ["marketing", "ads-summary", campaignFilter],
    () => api.get("/marketing/ads-logs/summary", { params: campaignFilter ? { campaign_id: campaignFilter } : {} }).then((r) => r.data),
    { enabled: bolehLihat },
  );

  const rows = useMemo(() => data || [], [data]);
  const sorted = sortRows(rows, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 15);
  const halaman = sorted.slice(awal, akhir);

  const filterAktif = adaFilterAktif(campaignFilter, platformFilter, pembuatFilter, q, dateFrom, dateTo);
  const resetFilter = () => {
    setCampaignFilter(""); setPlatformFilter(""); setPembuatFilter("");
    setQ(""); setDateFrom(""); setDateTo("");
  };

  const eksporCsv = () => {
    unduhCsv(`ads-log-${hariIniISO()}.csv`, [
      { label: "Tanggal", value: (r) => r.tanggal },
      { label: "Campaign", value: (r) => namaCampaign(campaignMap, r.campaign_id) },
      { label: "Platform", value: (r) => PLATFORM_LABEL[r.platform] || r.platform },
      { label: "Spend", value: (r) => r.spend },
      { label: "Impressions", value: (r) => r.impressions },
      { label: "Klik", value: (r) => r.clicks },
      { label: "Leads", value: (r) => r.leads },
      { label: "Konversi", value: (r) => r.conversions },
      { label: "Video Views", value: (r) => r.video_views },
      { label: "Engagements", value: (r) => r.engagements },
    ], sorted);
  };

  const bukaTambah = () => { setEditId(null); setForm(emptyForm()); };
  const bukaEdit = (r) => {
    setEditId(r.id);
    setForm({ ...r });
  };

  const simpan = async () => {
    if (!form.campaign_id) { toast.error("Pilih campaign dulu"); return; }
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/marketing/ads-logs/${editId}`, {
          spend: form.spend, impressions: form.impressions, clicks: form.clicks, leads: form.leads,
          conversions: form.conversions, video_views: form.video_views, engagements: form.engagements, catatan: form.catatan,
        });
        toast.success("Log ads diperbarui");
      } else {
        await api.post("/marketing/ads-logs", form);
        toast.success("Log ads ditambahkan");
      }
      setForm(null); setEditId(null); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan log ads"); }
    finally { setBusy(false); }
  };

  const hapus = async (r) => {
    if (!window.confirm(`Hapus log ads ${PLATFORM_LABEL[r.platform]} tanggal ${formatTanggal(r.tanggal)}?`)) return;
    const aksi = (pin) => api.delete(`/marketing/ads-logs/${r.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_ads", targetId: r.id, ringkasan: "Hapus log ads" });
    if (langsung) { toast.success("Log ads dihapus"); refetch(); }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Radio} title="Paid Ads" />
        <TidakPunyaAkses label="Paid Ads" testid="mkt-ads-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-ads">
      <PageHeader
        icon={Radio} title="Paid Ads"
        desc="Log harian performa iklan (Meta/Google/TikTok Ads) — murni metrik, tidak membuat jurnal."
        actions={<>
          <TombolEksporCsv onClick={eksporCsv} testid="mkt-ads-export" />
          {bolehTambah && (
            <button onClick={bukaTambah} disabled={!bolehCampaign || campaignList.length === 0} data-testid="mkt-ads-tambah"
                    title={!bolehCampaign ? "Butuh akses lihat Campaign untuk memilih campaign tujuan" : campaignList.length === 0 ? "Belum ada campaign yang bisa dipilih" : undefined}
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
              <Plus className="w-4 h-4" /> Tambah Log
            </button>
          )}
        </>}
      />

      {summary && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid="mkt-ads-summary">
          <p className="text-sm font-semibold text-slate-900 mb-2">Ringkasan per Platform ({summary.total_hari} entri harian)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(summary.per_platform || {}).map(([p, s]) => (
              <div key={p} className="border border-slate-100 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-700">{PLATFORM_LABEL[p] || p}</p>
                <p className="text-sm font-mono text-slate-900 mt-1">{idr(s.spend)}</p>
                <p className="text-[11px] text-slate-500 mt-1">
                  {fmtNum(s.impressions)} impr · {fmtNum(s.clicks)} klik · {fmtNum(s.leads)} lead
                </p>
              </div>
            ))}
            {Object.keys(summary.per_platform || {}).length === 0 && <p className="text-sm text-slate-400">Belum ada data.</p>}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <SearchInput value={q} onChange={setQ} placeholder="Cari catatan log..." testid="mkt-ads-cari" className="w-56" />
        {bolehCampaign && (
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="mkt-ads-filter-campaign" className={`${selectCls} h-9 w-auto`}>
            <option value="">Semua Campaign</option>
            {campaignList.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
          </select>
        )}
        <FilterSelect value={platformFilter} onChange={setPlatformFilter} testid="mkt-ads-filter-platform"
                      semuaLabel="Semua Platform"
                      opsi={ADS_PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABEL[p] }))} />
        <FilterSelect value={pembuatFilter} onChange={setPembuatFilter} testid="mkt-ads-filter-pembuat"
                      semuaLabel="Semua User" opsi={opsi.dibuat_oleh} />
        <FilterRentangTanggal dari={dateFrom} sampai={dateTo} onDari={setDateFrom} onSampai={setDateTo}
                              testid="mkt-ads-filter" labelDari="Dari tanggal" labelSampai="Sampai tanggal" />
        <TombolResetFilter aktif={filterAktif} onReset={resetFilter} testid="mkt-ads-reset-filter" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-ads-sort-tanggal" />
                <th className="px-3 py-2.5 font-semibold">Campaign</th>
                <th className="px-3 py-2.5 font-semibold">Platform</th>
                <SortTh label="Spend" sortKeyName="spend" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-ads-sort-spend" />
                <th className="px-3 py-2.5 font-semibold text-right">Impr</th>
                <th className="px-3 py-2.5 font-semibold text-right">Klik</th>
                <th className="px-3 py-2.5 font-semibold text-right">Lead</th>
                <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                            varian="tabel" kolom={8} pesanKosong="Belum ada log ads." data-testid="mkt-ads-state">
                {halaman.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`mkt-ads-row-${r.id}`}>
                    <td className="px-3 py-2.5 text-slate-700">{formatTanggal(r.tanggal)}</td>
                    <td className="px-3 py-2.5 text-slate-600 text-xs">{namaCampaign(campaignMap, r.campaign_id)}</td>
                    <td className="px-3 py-2.5 text-slate-600">{PLATFORM_LABEL[r.platform] || r.platform}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{idr(r.spend)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtNum(r.impressions)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtNum(r.clicks)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtNum(r.leads)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {bolehEdit && (
                          <button onClick={() => bukaEdit(r)} data-testid={`mkt-ads-edit-${r.id}`}
                                  className="w-8 h-8 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"><Pencil className="w-4 h-4" /></button>
                        )}
                        {bolehHapus && (
                          <button onClick={() => hapus(r)} data-testid={`mkt-ads-hapus-${r.id}`}
                                  className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </div>
        <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-ads-paginasi" />
      </div>

      <Modal open={!!form} onClose={() => { setForm(null); setEditId(null); }} title={editId ? "Edit Log Ads" : "Tambah Log Ads"} testid="mkt-ads-modal"
             footer={<>
               <button onClick={() => { setForm(null); setEditId(null); }} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpan} disabled={busy} data-testid="mkt-ads-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Simpan"}</button>
             </>}>
        {form && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Campaign" required>
                <select value={form.campaign_id} disabled={!!editId} onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))}
                        data-testid="mkt-ads-f-campaign" className={selectCls}>
                  <option value="">— pilih campaign —</option>
                  {campaignList.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
                </select>
              </Field>
              <Field label="Platform" required>
                <select value={form.platform} disabled={!!editId} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}
                        data-testid="mkt-ads-f-platform" className={selectCls}>
                  {ADS_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Tanggal" required>
              <DatePicker value={form.tanggal} onChange={(v) => setForm((f) => ({ ...f, tanggal: v }))} disabled={!!editId} data-testid="mkt-ads-f-tanggal" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Spend (Rp)">
                <InputRupiah value={form.spend} onChange={(v) => setForm((f) => ({ ...f, spend: v }))} testid="mkt-ads-f-spend" className={inputCls} />
              </Field>
              <Field label="Impressions">
                <input type="number" min="0" value={form.impressions} onChange={(e) => setForm((f) => ({ ...f, impressions: Number(e.target.value || 0) }))} data-testid="mkt-ads-f-impressions" className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Klik">
                <input type="number" min="0" value={form.clicks} onChange={(e) => setForm((f) => ({ ...f, clicks: Number(e.target.value || 0) }))} data-testid="mkt-ads-f-clicks" className={inputCls} />
              </Field>
              <Field label="Leads">
                <input type="number" min="0" value={form.leads} onChange={(e) => setForm((f) => ({ ...f, leads: Number(e.target.value || 0) }))} data-testid="mkt-ads-f-leads" className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Konversi">
                <input type="number" min="0" value={form.conversions} onChange={(e) => setForm((f) => ({ ...f, conversions: Number(e.target.value || 0) }))} data-testid="mkt-ads-f-conversions" className={inputCls} />
              </Field>
              <Field label="Video Views">
                <input type="number" min="0" value={form.video_views} onChange={(e) => setForm((f) => ({ ...f, video_views: Number(e.target.value || 0) }))} data-testid="mkt-ads-f-views" className={inputCls} />
              </Field>
              <Field label="Engagements">
                <input type="number" min="0" value={form.engagements} onChange={(e) => setForm((f) => ({ ...f, engagements: Number(e.target.value || 0) }))} data-testid="mkt-ads-f-engagements" className={inputCls} />
              </Field>
            </div>
            <Field label="Catatan">
              <textarea value={form.catatan} rows={2} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} data-testid="mkt-ads-f-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(refetch)} />
    </div>
  );
}
